/**
 * Investigation store — SQLite-backed CRUD for investigations,
 * collection tasks, and investigation logs.
 *
 * Handles JSON serialization for scope_contract, decomposed, coverage,
 * query, expected_entity_types, and detail fields.
 */

import { getDatabase } from '@/storage/database';
import type {
  Investigation,
  InvestigationState,
  InvestigationTierName,
  LifecycleState,
  CollectionTask,
  CollectionTaskStatus,
  LogEntry,
  LogAction,
  ScopeContract,
  CoverageReport,
} from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function db() {
  const conn = getDatabase();
  if (!conn) throw new Error('Database not initialized');
  return conn;
}

function now(): string {
  return new Date().toISOString();
}

function uuid(): string {
  return crypto.randomUUID();
}

/** Safely parse a JSON string, returning null on failure. */
function parseJSON<T>(text: string | null | undefined): T | null {
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Row types — DB column shapes (snake_case, TEXT for JSON)
// ---------------------------------------------------------------------------

interface InvestigationRow {
  id: string;
  title: string;
  raw_query: string;
  decomposed: string | null;
  state: string;
  desk_id: string;
  tier: string;
  scope_contract: string | null;
  coverage: string | null;
  token_usage: number;
  node_count: number;
  edge_count: number;
  started_at: string;
  completed_at: string | null;
  updated_at: string;
  lifecycle_state: string | null;
  last_activity_at: string | null;
}

interface CollectionTaskRow {
  id: string;
  investigation_id: string;
  source_id: string;
  query: string | null;
  expected_entity_types: string | null;
  status: string;
  raw_data: string | null;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
}

interface LogEntryRow {
  id: string;
  investigation_id: string;
  timestamp: string;
  state: string;
  action: string;
  detail: string | null;
  agent_id: string | null;
}

interface InvestigationEntityRow {
  id: string;
  investigation_id: string;
  entity_id: string;
  added_at: string;
}

// ---------------------------------------------------------------------------
// Row → domain converters
// ---------------------------------------------------------------------------

function rowToInvestigation(row: InvestigationRow): Investigation {
  return {
    id: row.id,
    title: row.title,
    rawQuery: row.raw_query,
    decomposed: parseJSON<ScopeContract>(row.decomposed),
    state: row.state as InvestigationState,
    deskId: row.desk_id,
    tier: row.tier as InvestigationTierName,
    scopeContract: parseJSON<ScopeContract>(row.scope_contract),
    coverage: parseJSON<CoverageReport>(row.coverage),
    tokenUsage: row.token_usage,
    nodeCount: row.node_count,
    edgeCount: row.edge_count,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    updatedAt: row.updated_at,
    lifecycleState: (row.lifecycle_state as LifecycleState) ?? 'review',
    lastActivityAt: row.last_activity_at ?? undefined,
  };
}

function rowToCollectionTask(row: CollectionTaskRow): CollectionTask {
  return {
    id: row.id,
    investigationId: row.investigation_id,
    sourceId: row.source_id,
    query: parseJSON<Record<string, unknown>>(row.query) ?? {},
    expectedEntityTypes: parseJSON<string[]>(row.expected_entity_types) ?? [],
    status: row.status as CollectionTaskStatus,
    rawData: row.raw_data,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    error: row.error,
  };
}

function rowToLogEntry(row: LogEntryRow): LogEntry {
  return {
    id: row.id,
    investigationId: row.investigation_id,
    timestamp: row.timestamp,
    state: row.state as InvestigationState,
    action: row.action as LogAction,
    detail: parseJSON<Record<string, unknown>>(row.detail) ?? {},
    agentId: row.agent_id,
  };
}

// ---------------------------------------------------------------------------
// Investigation CRUD
// ---------------------------------------------------------------------------

export async function createInvestigation(params: {
  title: string;
  rawQuery: string;
  deskId: string;
  tier: InvestigationTierName;
}): Promise<Investigation> {
  const id = `inv_${uuid()}`;
  const ts = now();

  await db().execute(
    `INSERT INTO investigations (id, title, raw_query, state, desk_id, tier, token_usage, node_count, edge_count, started_at, updated_at, lifecycle_state, last_activity_at)
     VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13)`,
    [id, params.title, params.rawQuery, 'init', params.deskId, params.tier, 0, 0, 0, ts, ts, 'review', ts],
  );

  return {
    id,
    title: params.title,
    rawQuery: params.rawQuery,
    decomposed: null,
    state: 'init',
    deskId: params.deskId,
    tier: params.tier,
    scopeContract: null,
    coverage: null,
    tokenUsage: 0,
    nodeCount: 0,
    edgeCount: 0,
    startedAt: ts,
    completedAt: null,
    updatedAt: ts,
    lifecycleState: 'review',
    lastActivityAt: ts,
  };
}

export async function getInvestigation(id: string): Promise<Investigation | null> {
  const rows = await db().query<InvestigationRow>(
    'SELECT * FROM investigations WHERE id = $1',
    [id],
  );
  if (rows.length === 0) return null;
  return rowToInvestigation(rows[0]);
}

export async function updateInvestigation(
  id: string,
  updates: Partial<{
    state: InvestigationState;
    decomposed: ScopeContract | null;
    scopeContract: ScopeContract | null;
    coverage: CoverageReport | null;
    tokenUsage: number;
    nodeCount: number;
    edgeCount: number;
    completedAt: string | null;
    lifecycleState: LifecycleState;
    lastActivityAt: string;
  }>,
): Promise<void> {
  const sets: string[] = [];
  const params: unknown[] = [];
  let idx = 1;

  if (updates.state !== undefined) {
    sets.push(`state = $${idx++}`);
    params.push(updates.state);
  }
  if (updates.decomposed !== undefined) {
    sets.push(`decomposed = $${idx++}`);
    params.push(updates.decomposed ? JSON.stringify(updates.decomposed) : null);
  }
  if (updates.scopeContract !== undefined) {
    sets.push(`scope_contract = $${idx++}`);
    params.push(updates.scopeContract ? JSON.stringify(updates.scopeContract) : null);
  }
  if (updates.coverage !== undefined) {
    sets.push(`coverage = $${idx++}`);
    params.push(updates.coverage ? JSON.stringify(updates.coverage) : null);
  }
  if (updates.tokenUsage !== undefined) {
    sets.push(`token_usage = $${idx++}`);
    params.push(updates.tokenUsage);
  }
  if (updates.nodeCount !== undefined) {
    sets.push(`node_count = $${idx++}`);
    params.push(updates.nodeCount);
  }
  if (updates.edgeCount !== undefined) {
    sets.push(`edge_count = $${idx++}`);
    params.push(updates.edgeCount);
  }
  if (updates.completedAt !== undefined) {
    sets.push(`completed_at = $${idx++}`);
    params.push(updates.completedAt);
  }
  if (updates.lifecycleState !== undefined) {
    sets.push(`lifecycle_state = $${idx++}`);
    params.push(updates.lifecycleState);
  }
  if (updates.lastActivityAt !== undefined) {
    sets.push(`last_activity_at = $${idx++}`);
    params.push(updates.lastActivityAt);
  }

  if (sets.length === 0) return;

  sets.push(`updated_at = $${idx++}`);
  params.push(now());

  params.push(id);

  await db().execute(
    `UPDATE investigations SET ${sets.join(', ')} WHERE id = $${idx}`,
    params,
  );
}

export async function listInvestigations(filters?: {
  state?: InvestigationState;
  deskId?: string;
  limit?: number;
}): Promise<Investigation[]> {
  const conditions: string[] = [];
  const params: unknown[] = [];
  let idx = 1;

  if (filters?.state) {
    conditions.push(`state = $${idx++}`);
    params.push(filters.state);
  }
  if (filters?.deskId) {
    conditions.push(`desk_id = $${idx++}`);
    params.push(filters.deskId);
  }

  let sql = 'SELECT * FROM investigations';
  if (conditions.length > 0) {
    sql += ' WHERE ' + conditions.join(' AND ');
  }
  sql += ' ORDER BY started_at DESC';

  if (filters?.limit) {
    sql += ` LIMIT $${idx++}`;
    params.push(filters.limit);
  }

  const rows = await db().query<InvestigationRow>(sql, params);
  return rows.map(rowToInvestigation);
}

// ---------------------------------------------------------------------------
// Collection task CRUD
// ---------------------------------------------------------------------------

export async function createCollectionTask(task: CollectionTask): Promise<void> {
  await db().execute(
    `INSERT INTO collection_tasks (id, investigation_id, source_id, query, expected_entity_types, status, raw_data, started_at, completed_at, error)
     VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)`,
    [
      task.id,
      task.investigationId,
      task.sourceId,
      JSON.stringify(task.query),
      JSON.stringify(task.expectedEntityTypes),
      task.status,
      task.rawData,
      task.startedAt,
      task.completedAt,
      task.error,
    ],
  );
}

export async function updateCollectionTask(
  id: string,
  updates: Partial<{
    status: CollectionTaskStatus;
    rawData: string | null;
    startedAt: string | null;
    completedAt: string | null;
    error: string | null;
  }>,
): Promise<void> {
  const sets: string[] = [];
  const params: unknown[] = [];
  let idx = 1;

  if (updates.status !== undefined) {
    sets.push(`status = $${idx++}`);
    params.push(updates.status);
  }
  if (updates.rawData !== undefined) {
    sets.push(`raw_data = $${idx++}`);
    params.push(updates.rawData);
  }
  if (updates.startedAt !== undefined) {
    sets.push(`started_at = $${idx++}`);
    params.push(updates.startedAt);
  }
  if (updates.completedAt !== undefined) {
    sets.push(`completed_at = $${idx++}`);
    params.push(updates.completedAt);
  }
  if (updates.error !== undefined) {
    sets.push(`error = $${idx++}`);
    params.push(updates.error);
  }

  if (sets.length === 0) return;
  params.push(id);

  await db().execute(
    `UPDATE collection_tasks SET ${sets.join(', ')} WHERE id = $${idx}`,
    params,
  );
}

export async function getCollectionTasks(investigationId: string): Promise<CollectionTask[]> {
  const rows = await db().query<CollectionTaskRow>(
    'SELECT * FROM collection_tasks WHERE investigation_id = $1 ORDER BY started_at ASC',
    [investigationId],
  );
  return rows.map(rowToCollectionTask);
}

export async function getCollectionTask(id: string): Promise<CollectionTask | null> {
  const rows = await db().query<CollectionTaskRow>(
    'SELECT * FROM collection_tasks WHERE id = $1',
    [id],
  );
  if (rows.length === 0) return null;
  return rowToCollectionTask(rows[0]);
}

// ---------------------------------------------------------------------------
// Investigation log CRUD
// ---------------------------------------------------------------------------

export async function addLogEntry(params: {
  investigationId: string;
  state: InvestigationState;
  action: LogAction;
  detail: Record<string, unknown>;
  agentId?: string | null;
}): Promise<LogEntry> {
  const id = `log_${uuid()}`;
  const ts = now();

  await db().execute(
    `INSERT INTO investigation_log (id, investigation_id, timestamp, state, action, detail, agent_id)
     VALUES ($1, $2, $3, $4, $5, $6, $7)`,
    [
      id,
      params.investigationId,
      ts,
      params.state,
      params.action,
      JSON.stringify(params.detail),
      params.agentId ?? null,
    ],
  );

  return {
    id,
    investigationId: params.investigationId,
    timestamp: ts,
    state: params.state,
    action: params.action,
    detail: params.detail,
    agentId: params.agentId ?? null,
  };
}

export async function getLogEntries(investigationId: string): Promise<LogEntry[]> {
  const rows = await db().query<LogEntryRow>(
    'SELECT * FROM investigation_log WHERE investigation_id = $1 ORDER BY timestamp ASC',
    [investigationId],
  );
  return rows.map(rowToLogEntry);
}

// ---------------------------------------------------------------------------
// Investigation ↔ Entity linking
// ---------------------------------------------------------------------------

export async function linkEntityToInvestigation(
  investigationId: string,
  entityId: string,
): Promise<void> {
  const id = `ie_${uuid()}`;
  await db().execute(
    `INSERT OR IGNORE INTO investigation_entities (id, investigation_id, entity_id, added_at)
     VALUES ($1, $2, $3, $4)`,
    [id, investigationId, entityId, now()],
  );
}

export async function getInvestigationEntityIds(investigationId: string): Promise<string[]> {
  const rows = await db().query<InvestigationEntityRow>(
    'SELECT * FROM investigation_entities WHERE investigation_id = $1',
    [investigationId],
  );
  return rows.map(r => r.entity_id);
}

// ---------------------------------------------------------------------------
// Lifecycle helpers
// ---------------------------------------------------------------------------

/** Update last_activity_at to now — call on every analyst interaction. */
export async function updateLastActivity(investigationId: string): Promise<void> {
  const ts = now();
  await db().execute(
    `UPDATE investigations SET last_activity_at = $1, updated_at = $2 WHERE id = $3`,
    [ts, ts, investigationId],
  );
}

/** Find investigations that are 'active' but haven't been touched in N days. */
export async function getIdleInvestigations(thresholdDays: number): Promise<Investigation[]> {
  const rows = await db().query<InvestigationRow>(
    `SELECT * FROM investigations
     WHERE lifecycle_state = 'active'
     AND last_activity_at < datetime('now', $1)
     ORDER BY last_activity_at ASC`,
    [`-${thresholdDays} days`],
  );
  return rows.map(rowToInvestigation);
}

// ---------------------------------------------------------------------------
// Assessment version tracking
// ---------------------------------------------------------------------------

interface AssessmentVersionRow {
  id: string;
  investigation_id: string;
  version: number;
  content: string;
  created_at: string;
  created_by: string;
}

/**
 * Save a new assessment version. Returns the version number.
 * Each edit creates a new version — previous versions are preserved.
 */
export async function saveAssessmentVersion(
  investigationId: string,
  content: Record<string, unknown>,
  createdBy: string = 'system',
): Promise<number> {
  const rows = await db().query<{ maxVersion: number }>(
    `SELECT COALESCE(MAX(version), 0) as maxVersion FROM assessment_versions WHERE investigation_id = $1`,
    [investigationId],
  );
  const nextVersion = (rows[0]?.maxVersion ?? 0) + 1;
  const id = `av_${uuid()}`;

  await db().execute(
    `INSERT INTO assessment_versions (id, investigation_id, version, content, created_at, created_by)
     VALUES ($1, $2, $3, $4, $5, $6)`,
    [id, investigationId, nextVersion, JSON.stringify(content), now(), createdBy],
  );
  return nextVersion;
}

/** Get all assessment versions for an investigation, newest first. */
export async function getAssessmentVersions(
  investigationId: string,
): Promise<Array<{ version: number; createdAt: string; createdBy: string }>> {
  const rows = await db().query<AssessmentVersionRow>(
    `SELECT version, created_at, created_by FROM assessment_versions
     WHERE investigation_id = $1 ORDER BY version DESC`,
    [investigationId],
  );
  return rows.map(r => ({
    version: r.version,
    createdAt: r.created_at,
    createdBy: r.created_by,
  }));
}

/** Get a specific assessment version content. */
export async function getAssessmentVersion(
  investigationId: string,
  version: number,
): Promise<Record<string, unknown> | null> {
  const rows = await db().query<AssessmentVersionRow>(
    `SELECT content FROM assessment_versions
     WHERE investigation_id = $1 AND version = $2`,
    [investigationId, version],
  );
  if (rows.length === 0) return null;
  return parseJSON<Record<string, unknown>>(rows[0].content);
}
